'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Grid3X3,
  Users,
  ShieldCheck,
  GraduationCap,
  Calendar,
  CheckSquare,
  Upload,
  Camera,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Edit2,
  Search,
  Check,
  X,
  MessageCircle,
  FileText,
  MessageSquare,
  MapPin,
  CalendarCheck,
  Eye,
  RefreshCw,
  Home,
  Sparkles,
  Plus,
  Trash2,
  Save,
  Wand2
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { ProgressSteps } from '@/components/ProgressSteps';
import { ImageCropper } from '@/components/ImageCropper';
import { AppShell3 } from '@/components/app-shell-3';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { LocationMapPicker } from '@/components/LocationMapPicker';
import { toast } from 'sonner';

const spaceTools = [
  {
    id: 'matrix',
    name: 'Matrix',
    icon: Grid3X3,
    description:
      'Create and manage training competency matrices to track skills and certifications across your workforce.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'team',
    name: 'Team',
    icon: Users,
    description:
      'Manage team members, roles, and responsibilities. Track individual progress and assignments.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'qualifications',
    name: 'Qualifications',
    icon: GraduationCap,
    description:
      'Manage training requirements, qualifications, and certifications needed for your workforce.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'team_compliance',
    name: 'Team Compliance',
    icon: ShieldCheck,
    description:
      'AI-powered team compliance tracking to monitor training requirements and ensure workforce adherence to standards.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'site_compliance',
    name: 'Site Compliance',
    icon: ShieldCheck,
    description:
      'Track site-specific compliance requirements and ensure locations meet regulatory and safety standards.',
    defaultEnabled: true
  },
  {
    id: 'bookings',
    name: 'Bookings',
    icon: Calendar,
    description:
      'Schedule and manage training sessions, workshops, and resource bookings with availability tracking.',
    defaultEnabled: true,
    essential: true
  },
  {
    id: 'message_board',
    name: 'Posts',
    icon: MessageSquare,
    description:
      'Post announcements, updates, and discussions. Keep everyone informed with threaded conversations.',
    defaultEnabled: true
  },
  {
    id: 'tasks',
    name: 'Tasks',
    icon: CheckSquare,
    description:
      'Create and manage tasks for training completion, compliance requirements, and space deliverables.',
    defaultEnabled: true
  },
  {
    id: 'docs',
    name: 'Docs',
    icon: FileText,
    description:
      'Store, organize, and collaborate on documents, training materials, and important files.',
    defaultEnabled: true
  },
  {
    id: 'checkins',
    name: 'Check-Ins',
    icon: MapPin,
    description:
      'QR code-based check-ins for sites. Track attendance and presence at physical locations.',
    defaultEnabled: true
  },
  {
    id: 'calendar',
    name: 'Calendar',
    icon: Calendar,
    description:
      'Set important dates, deadlines, and milestones. Sync with external calendars and get reminders.',
    defaultEnabled: true
  },
  {
    id: 'chat',
    name: 'Chat',
    icon: MessageCircle,
    description:
      'Real-time team communication with direct messages, group chats, and file sharing capabilities.',
    defaultEnabled: true
  },
  {
    id: 'attendance',
    name: 'Attendance',
    icon: CalendarCheck,
    description:
      'Track team attendance, manage leave requests, bank holidays, and shift patterns.',
    defaultEnabled: true
  }
];

const progressSteps = [
  { title: 'Space basics', description: 'Name and details' },
  { title: 'Choose tools', description: 'Select features' },
  { title: 'Add team', description: 'Invite members' }
];

const presetColors = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Yellow', value: '#FEF08A' },
  { name: 'Orange', value: '#FED7AA' },
  { name: 'Pink', value: '#FECACA' },
  { name: 'Rose', value: '#F9A8D4' },
  { name: 'Purple', value: '#DDD6FE' },
  { name: 'Blue', value: '#BFDBFE' },
  { name: 'Green', value: '#BBF7D0' },
  { name: 'Gray', value: '#E5E7EB' },
  { name: 'Slate', value: '#CBD5E1' }
];

const imageStyles = [
  {
    id: 'vector',
    name: 'Flat Vector',
    description: 'Clean, modern flat design with solid colors',
    prompt:
      'flat vector illustration style, minimalist, clean lines, solid colors'
  },
  {
    id: 'gradient',
    name: 'Gradient Modern',
    description: 'Modern design with smooth color gradients',
    prompt: 'modern gradient style, smooth color transitions, contemporary look'
  },
  {
    id: '3d',
    name: '3D Render',
    description: 'Three-dimensional rendered objects with depth',
    prompt: '3D rendered style, depth and shadows, polished surfaces'
  },
  {
    id: 'cartoon',
    name: 'Cartoon',
    description: 'Playful cartoon illustration style',
    prompt: 'cartoon illustration style, playful, fun, bold outlines'
  },
  {
    id: 'realistic',
    name: 'Photorealistic',
    description: 'Realistic photography style',
    prompt: 'photorealistic style, detailed, natural lighting, high quality'
  },
  {
    id: 'line-art',
    name: 'Line Art',
    description: 'Simple line drawings with minimal color',
    prompt: 'line art style, clean outlines, minimal detail, elegant simplicity'
  },
  {
    id: 'abstract',
    name: 'Abstract',
    description: 'Abstract shapes and patterns',
    prompt: 'abstract geometric style, shapes and patterns, artistic'
  },
  {
    id: 'isometric',
    name: 'Isometric',
    description: 'Isometric 3D perspective design',
    prompt: 'isometric 3D style, angled perspective, clean geometric shapes'
  }
];

const CreateNewSpace = () => {
  const navigate = useNavigate();
  const { company, userProfile, canCreateSpaces, loading: authLoading, permissionsLoaded } = useAuth();
  usePageTitle('Create Space');
  const [currentStep, setCurrentStep] = useState(1);

  // Auth is ready when not loading AND permissions have been loaded
  const authReady = !authLoading && permissionsLoaded;

  // Redirect non-authorized users (only after auth has loaded)
  useEffect(() => {
    if (authReady && !canCreateSpaces()) {
      toast.error('You do not have permission to create spaces');
      navigate('/dashboard');
    }
  }, [authReady, canCreateSpaces, navigate]);
  const [animationDirection, setAnimationDirection] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createdSpace, setCreatedSpace] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    locationData: null,
    industry: '',
    avatar: '',
    color: '#FFFFFF',
    schedule: {
      startDate: '',
      endDate: ''
    },
    tools: spaceTools.reduce((acc, tool) => {
      acc[tool.id] = tool.defaultEnabled;
      return acc;
    }, {}),
    teamMembers: [],
    visibilityType: 'invite_only',
    customFields: {}
  });

  const [previewImage, setPreviewImage] = useState('');
  const [tempImageSrc, setTempImageSrc] = useState('');
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isColorPopoverOpen, setIsColorPopoverOpen] = useState(false);
  const [isCustomFieldsEnabled, setIsCustomFieldsEnabled] = useState(false);
  const [isAddingCustomField, setIsAddingCustomField] = useState(false);
  const [newCustomFieldName, setNewCustomFieldName] = useState('');
  const [newCustomFieldValue, setNewCustomFieldValue] = useState('');
  const [isAiGeneratorOpen, setIsAiGeneratorOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiColors, setAiColors] = useState([]);
  const [aiStyle, setAiStyle] = useState('vector');
  const [includeSpaceName, setIncludeSpaceName] = useState(true);
  const [includeSpaceDescription, setIncludeSpaceDescription] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [projectUuid, setProjectUuid] = useState(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch team members from the same company
  useEffect(() => {
    if (company?.id) {
      fetchTeamMembers();
    }
  }, [company?.id]);

  const fetchTeamMembers = async () => {
    if (!company?.id) return;

    console.log('Fetching team members for company:', company.id);
    setIsLoadingTeam(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(
          'id, email, first_name, last_name, avatar_url, company_id, auth_id'
        )
        .eq('company_id', company.id)
        .not('auth_id', 'is', null);

      if (error) throw error;

      const formattedMembers =
        data?.map(member => ({
          id: member.id,
          name:
            `${member.first_name || ''} ${member.last_name || ''}`.trim() ||
            member.email,
          email: member.email,
          role: member.id === userProfile?.id ? '(You)' : '',
          avatar: member.avatar_url || null,
          initials: `${(member.first_name || member.email)[0]}${
            (member.last_name || '')[0] || ''
          }`.toUpperCase(),
          isCurrentUser: member.id === userProfile?.id
        })) || [];

      setTeamMembers(formattedMembers);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoadingTeam(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleToolToggle = toolId => {
    setFormData(prev => ({
      ...prev,
      tools: {
        ...prev.tools,
        [toolId]: !prev.tools[toolId]
      }
    }));
  };

  const handleTeamMemberToggle = member => {
    setFormData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.find(m => m.id === member.id)
        ? prev.teamMembers.filter(m => m.id !== member.id)
        : [...prev.teamMembers, member]
    }));
  };

  // Handle location data changes from LocationMapPicker
  const handleLocationChange = useCallback(data => {
    setFormData(prev => ({
      ...prev,
      locationData: data,
      // Also update the text location field for backwards compatibility
      location: data?.mainAddress || prev.location
    }));
  }, []);

  const handleCustomFieldChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [key]: value
      }
    }));
  };

  const handleDeleteCustomField = key => {
    setFormData(prev => {
      const newCustomFields = { ...prev.customFields };
      delete newCustomFields[key];
      return {
        ...prev,
        customFields: newCustomFields
      };
    });
  };

  const handleAddCustomField = () => {
    setIsAddingCustomField(true);
  };

  const handleSaveNewCustomField = () => {
    if (newCustomFieldName.trim()) {
      setFormData(prev => ({
        ...prev,
        customFields: {
          ...prev.customFields,
          [newCustomFieldName.trim()]: newCustomFieldValue.trim()
        }
      }));
      setNewCustomFieldName('');
      setNewCustomFieldValue('');
      setIsAddingCustomField(false);
    }
  };

  const handleCancelAddCustomField = () => {
    setNewCustomFieldName('');
    setNewCustomFieldValue('');
    setIsAddingCustomField(false);
  };

  const handleImageUpload = event => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        setTempImageSrc(e.target.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const handleCropComplete = croppedImage => {
    setPreviewImage(croppedImage);
    setFormData(prev => ({
      ...prev,
      avatar: croppedImage
    }));
    setIsCropperOpen(false);
    setTempImageSrc('');
  };

  const handleGenerateAiImage = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter a prompt for the AI image generator');
      return;
    }

    setIsGenerating(true);
    try {
      // Generate a UUID for this project (or reuse existing one)
      const uuid = projectUuid || generateUUID();
      if (!projectUuid) {
        setProjectUuid(uuid);
      }

      // Get selected style details
      const selectedStyle = imageStyles.find(s => s.id === aiStyle);

      // Build a natural, human-readable prompt for the AI
      let fullPrompt =
        'Generate a professional 1:1 square avatar image (1024x1024px) suitable for a workspace/project logo. ';
      fullPrompt +=
        'The image should work well at small sizes (like thumbnails) and look good in a circular crop. ';

      // Add the main user description
      fullPrompt += `\n\nImage description: ${aiPrompt.trim()}`;

      // Add space context if toggled on
      if (includeSpaceName && formData.name) {
        fullPrompt += `\n\nThis avatar is for a workspace called "${formData.name}"`;
        if (includeSpaceDescription && formData.description) {
          fullPrompt += ` - ${formData.description}`;
        }
        fullPrompt +=
          '. Make sure the image represents this workspace appropriately.';
      } else if (includeSpaceDescription && formData.description) {
        fullPrompt += `. Context: ${formData.description}`;
      }

      // Add color requirements if selected
      if (aiColors.length > 0) {
        const colorNames = aiColors.map(colorValue => {
          const color = presetColors.find(c => c.value === colorValue);
          return color ? color.name : colorValue;
        });
        fullPrompt += `\n\nImportant: Prominently incorporate these colors in the design: ${colorNames.join(
          ', '
        )}.`;
      }

      // Add style requirements
      fullPrompt += `\n\nStyle: Create this in a ${selectedStyle.prompt}. `;
      fullPrompt +=
        'IMPORTANT: Do not include any text, letters, numbers, or words in the image whatsoever. Ensure good contrast and visibility. Keep it professional and suitable for business use.';

      // Generate unique image name using timestamp and random string
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const imageName = `avatar-${timestamp}-${randomStr}.png`;

      console.log('🎨 Generating AI image with prompt:', fullPrompt);
      console.log('📋 Company ID:', company?.id);
      console.log('🆔 Project UUID:', uuid);
      console.log('🖼️ Image Name:', imageName);
      console.log(
        '🌐 Calling N8N webhook: https://n8n.add.ai/webhook/image-generator-SM'
      );

      // Call N8N webhook
      const response = await fetch(
        'https://n8n.add.ai/webhook/image-generator-SM',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prompt: fullPrompt,
            company_id: company?.id,
            project_id: uuid,
            image_name: imageName
          })
        }
      );

      console.log('📡 Response status:', response.status, response.statusText);

      const data = await response.json();
      console.log('🖼️ AI Image generation response:', data);

      // Handle the Google Cloud Storage response
      if (data && data.publicUrl) {
        console.log('✅ Generated image URL:', data.publicUrl);

        // Store the generated image URL to show in preview
        setGeneratedImageUrl(data.publicUrl);

        // Success feedback - but keep dialog open to show preview
      } else {
        console.error('❌ Unexpected response format:', data);
        alert(
          'Image generated but response format was unexpected. Check console.'
        );
      }
    } catch (error) {
      console.error('Error generating AI image:', error);
      alert('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleColorToggle = colorValue => {
    setAiColors(prev =>
      prev.includes(colorValue)
        ? prev.filter(c => c !== colorValue)
        : [...prev, colorValue]
    );
  };

  const handleSaveGeneratedImage = () => {
    if (generatedImageUrl) {
      // Set the avatar in form data
      setFormData(prev => ({
        ...prev,
        avatar: generatedImageUrl
      }));

      // Set as preview image
      setPreviewImage(generatedImageUrl);

      // Close dialog and reset
      setIsAiGeneratorOpen(false);
      handleResetAiGenerator();
    }
  };

  const handleResetAiGenerator = () => {
    setAiPrompt('');
    setAiColors([]);
    setAiStyle('vector');
    setIncludeSpaceName(true);
    setIncludeSpaceDescription(true);
    setGeneratedImageUrl(null);
  };

  const handleCloseAiGenerator = open => {
    setIsAiGeneratorOpen(open);
    if (!open) {
      handleResetAiGenerator();
    }
  };

  const generateUUID = () => {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  const handleCreateSpace = async () => {
    if (!company?.id || !userProfile?.id) return;

    setIsCreating(true);
    try {
      console.log('🎨 Creating space with color:', formData.color);
      console.log('📋 Full formData:', formData);

      // Determine color name from preset or custom
      const selectedColor = presetColors.find(c => c.value === formData.color);
      const colorObject = {
        name: selectedColor ? selectedColor.name : 'custom',
        value: formData.color || '#FFFFFF'
      };

      // Create the project (use stored UUID if AI avatar was generated)
      const projectData = {
        company_id: company.id,
        name: formData.name,
        type: formData.type || 'General',
        description: formData.description || null,
        location: formData.location || null,
        location_data: formData.locationData || null,
        industry: formData.industry || null,
        avatar: formData.avatar || null,
        color: colorObject, // Project-level default color as JSONB object
        tools: formData.tools, // Send as object for JSONB column
        schedule: formData.schedule, // Send as object for JSONB column
        visibility_type: formData.visibilityType || 'invite_only',
        custom_fields: formData.customFields // Send as object for JSONB column
      };

      // If we generated an AI avatar, use that UUID for consistency with storage path
      if (projectUuid) {
        projectData.id = projectUuid;
        console.log('🆔 Using pre-generated UUID for project:', projectUuid);
      }

      const { data: createdProject, error: projectError } = await supabase
        .from('projects')
        .insert(projectData)
        .select()
        .single();

      if (projectError) {
        console.error('Error creating project:', projectError);
        alert('Failed to create space. Please try again.');
        return;
      }

      console.log('✅ Created project from database:', createdProject);
      console.log('🎨 Color in created project:', createdProject.color);

      // Create space_members records
      const membersToInsert = [];

      // Always add the creator as owner
      membersToInsert.push({
        project_id: createdProject.id,
        user_id: userProfile.id,
        role: 'Owner',
        created_by: userProfile.id
      });

      // If invite_only, add the invited team members
      if (
        formData.visibilityType === 'invite_only' &&
        formData.teamMembers?.length > 0
      ) {
        formData.teamMembers.forEach(member => {
          if (member.id !== userProfile.id) {
            membersToInsert.push({
              project_id: createdProject.id,
              user_id: member.id,
              role: 'Member',
              created_by: userProfile.id
            });
          }
        });
      }

      // Insert all project members
      const { error: membersError } = await supabase
        .from('space_members')
        .insert(membersToInsert);

      if (membersError) {
        console.error('Error adding project members:', membersError);
      }

      // Success! Show success state
      setCreatedSpace(createdProject);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error in handleCreateSpace:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleViewSpace = () => {
    if (createdSpace) {
      navigate(`/space/${createdSpace.id}`);
    }
  };

  const handleCreateAnother = () => {
    // Reset all state
    setFormData({
      name: '',
      description: '',
      location: '',
      locationData: null,
      industry: '',
      avatar: '',
      color: '#FFFFFF',
      schedule: { startDate: '', endDate: '' },
      tools: spaceTools.reduce((acc, tool) => {
        acc[tool.id] = tool.defaultEnabled;
        return acc;
      }, {}),
      teamMembers: [],
      visibilityType: 'invite_only',
      customFields: {}
    });
    setPreviewImage('');
    setCurrentStep(1);
    setAnimationDirection(0);
    setShowSuccess(false);
    setCreatedSpace(null);
    setIsCustomFieldsEnabled(false);
    setIsAddingCustomField(false);
    setNewCustomFieldName('');
    setProjectUuid(null); // Clear the UUID so a fresh one is generated for the next space
    setGeneratedImageUrl(null); // Clear any generated AI image
  };

  const handleReturnToDashboard = () => {
    navigate('/dashboard');
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setAnimationDirection(1);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setAnimationDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    if (currentStep === 1) {
      return formData.name.trim() !== '';
    }
    return true;
  };

  const ToolCard = ({ tool, isEnabled }) => {
    const IconComponent = tool.icon;
    const isEssential = tool.essential;

    return (
      <motion.div
        className={`border rounded-lg p-4 transition-all ${
          isEssential
            ? 'bg-gray-50 border-gray-300 cursor-not-allowed opacity-75'
            : isEnabled
            ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700 cursor-pointer'
            : 'bg-white hover:bg-gray-50 border-gray-200 cursor-pointer'
        }`}
        onClick={() => !isEssential && handleToolToggle(tool.id)}
        whileHover={!isEssential ? { scale: 1.02 } : {}}
        whileTap={!isEssential ? { scale: 0.98 } : {}}
      >
        <div className="flex items-start space-x-3">
          <div className={`flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 ${
            isEnabled
              ? 'bg-blue-600 border-blue-600'
              : 'border-gray-300'
          }`}>
            {isEnabled && (
              <Check className="h-3 w-3 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <IconComponent className={`h-4 w-4 ${isEnabled ? 'text-blue-600' : 'text-gray-500'}`} />
              <p className={`font-medium ${isEnabled ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900'}`}>
                {tool.name}
                {isEssential && <span className="ml-2 text-xs text-gray-500">(Essential)</span>}
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{tool.description}</p>
          </div>
        </div>
      </motion.div>
    );
  };

  // Show loading while auth is being verified
  if (!authReady) {
    return (
      <AppShell3 breadcrumbs={[]} pageTitle='' pageDescription=''>
        <div className='min-h-[calc(100vh-200px)] flex items-center justify-center'>
          <Loader2 className='h-8 w-8 animate-spin text-violet-600' />
        </div>
      </AppShell3>
    );
  }

  // Don't render anything if user doesn't have permission (redirect will happen via useEffect)
  if (!canCreateSpaces()) {
    return null;
  }

  // Success view
  if (showSuccess && createdSpace) {
    return (
      <AppShell3 breadcrumbs={[]} pageTitle='' pageDescription=''>
        <div className='min-h-[calc(100vh-200px)] flex items-center justify-center px-4'>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className='max-w-2xl w-full'
          >
            <Card className='border-2 border-green-200 bg-gradient-to-br from-green-50 to-white shadow-2xl'>
              <CardContent className='p-12 text-center space-y-8'>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className='inline-block p-6 bg-green-100 rounded-full'
                >
                  <Sparkles className='h-16 w-16 text-green-600' />
                </motion.div>

                <div className='space-y-3'>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className='text-3xl font-bold text-gray-900'
                  >
                    Space Created Successfully!
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className='text-lg text-gray-600'
                  >
                    Your space{' '}
                    <span className='font-semibold text-gray-900'>
                      "{createdSpace.name}"
                    </span>{' '}
                    is ready to use
                  </motion.p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className='flex flex-col sm:flex-row gap-4 justify-center pt-4'
                >
                  <Button
                    onClick={handleViewSpace}
                    size='lg'
                    className='bg-black hover:bg-gray-900 text-white shadow-lg hover:shadow-xl transition-all px-8'
                  >
                    <Eye className='h-5 w-5 mr-2' />
                    View Space
                  </Button>

                  <Button
                    onClick={handleCreateAnother}
                    size='lg'
                    variant='outline'
                    className='border-gray-300 hover:bg-gray-100 transition-all px-8'
                  >
                    <RefreshCw className='h-5 w-5 mr-2' />
                    Create Another
                  </Button>

                  <Button
                    onClick={handleReturnToDashboard}
                    size='lg'
                    variant='outline'
                    className='border-gray-300 hover:bg-gray-100 transition-all px-8'
                  >
                    <Home className='h-5 w-5 mr-2' />
                    Return to Dashboard
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3 breadcrumbs={[]} pageTitle='' pageDescription=''>
      <div className='max-w-6xl mx-auto px-4 py-8'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className='bg-white rounded-xl shadow-xl border overflow-hidden'
        >
          {/* Header Section */}
          <div className='relative bg-gradient-to-br from-gray-50 to-white border-b px-8 py-4'>
            {/* Back Button - Top Left */}
            <div className='absolute top-4 left-4'>
              <Button
                variant='ghost'
                size='sm'
                onClick={handleReturnToDashboard}
                className='gap-2'
              >
                <ArrowLeft className='h-4 w-4' />
                Back
              </Button>
            </div>

            <div className='max-w-4xl mx-auto'>
              {/* Title and Subtitle */}
              <div className='text-center mb-4'>
                <motion.h1
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className='text-3xl font-bold text-gray-900 mb-2'
                >
                  Create New Space
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className='text-gray-600'
                >
                  Set up your collaborative workspace in just a few steps
                </motion.p>
              </div>

              {/* Progress Steps */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <ProgressSteps
                  steps={progressSteps}
                  currentStep={currentStep}
                />
              </motion.div>
            </div>
          </div>

          {/* Content Area */}
          <div className='px-8 py-8'>
            <AnimatePresence mode='wait'>
              {/* Step 1: Space Basics */}
              {currentStep === 1 && (
                <motion.div
                  key='step1'
                  initial={{
                    opacity: 0,
                    x:
                      animationDirection === 0
                        ? 0
                        : animationDirection === -1
                        ? -100
                        : 100
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className='max-w-3xl mx-auto'
                >
                  <div className='space-y-4'>
                    {/* Avatar Upload and Color Picker */}
                    <div className='flex justify-between gap-8'>
                      {/* Avatar Upload */}
                      <div className='space-y-2 flex-1'>
                        <Label className='text-base'>Space Avatar</Label>
                        <div className='flex items-center space-x-4'>
                          <Avatar className='h-16 w-16'>
                            <AvatarImage
                              src={previewImage}
                              alt='Space avatar'
                              className='object-cover'
                            />
                            <AvatarFallback>
                              <Camera className='h-6 w-6' />
                            </AvatarFallback>
                          </Avatar>
                          <div className='space-y-2'>
                            {!previewImage ? (
                              <div className='flex gap-2'>
                                <Button
                                  type='button'
                                  variant='outline'
                                  onClick={() => fileInputRef.current?.click()}
                                  className='flex items-center space-x-2'
                                >
                                  <Upload className='h-4 w-4' />
                                  <span>Upload Image</span>
                                </Button>
                                <Button
                                  type='button'
                                  variant='outline'
                                  onClick={() => setIsAiGeneratorOpen(true)}
                                  className='flex items-center space-x-2 bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 border-purple-200'
                                >
                                  <Wand2 className='h-4 w-4 text-purple-600' />
                                  <span className='text-purple-700'>
                                    Generate with +Ai
                                  </span>
                                </Button>
                              </div>
                            ) : (
                              <div className='flex gap-2'>
                                <Button
                                  type='button'
                                  variant='outline'
                                  onClick={async () => {
                                    // If image is from Google Cloud Storage, convert to data URL first
                                    if (previewImage.includes('storage.googleapis.com')) {
                                      try {
                                        const response = await fetch(previewImage);
                                        const blob = await response.blob();
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          setTempImageSrc(reader.result);
                                          setIsCropperOpen(true);
                                        };
                                        reader.readAsDataURL(blob);
                                      } catch (error) {
                                        console.error('Error loading image for cropping:', error);
                                        alert('Failed to load image for editing. Please try again.');
                                      }
                                    } else {
                                      // Already a data URL, use directly
                                      setTempImageSrc(previewImage);
                                      setIsCropperOpen(true);
                                    }
                                  }}
                                  className='flex items-center space-x-2'
                                >
                                  <Edit2 className='h-4 w-4' />
                                  <span>Edit</span>
                                </Button>
                                <Button
                                  type='button'
                                  variant='outline'
                                  onClick={() => fileInputRef.current?.click()}
                                  className='flex items-center space-x-2'
                                >
                                  <Upload className='h-4 w-4' />
                                  <span>Change</span>
                                </Button>
                                <Button
                                  type='button'
                                  variant='outline'
                                  onClick={() => setIsAiGeneratorOpen(true)}
                                  className='flex items-center space-x-2 bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 border-purple-200'
                                >
                                  <Wand2 className='h-4 w-4 text-purple-600' />
                                  <span className='text-purple-700'>
                                    +Ai Generate
                                  </span>
                                </Button>
                              </div>
                            )}
                            <input
                              ref={fileInputRef}
                              type='file'
                              accept='image/*'
                              onChange={handleImageUpload}
                              className='hidden'
                            />
                            <p className='text-xs text-muted-foreground'>
                              PNG, JPG up to 2MB
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Color Picker */}
                      <div className='space-y-2 flex-1'>
                        <Label className='text-base'>
                          Space Color (optional)
                        </Label>
                        <Popover
                          open={isColorPopoverOpen}
                          onOpenChange={setIsColorPopoverOpen}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              type='button'
                              variant='outline'
                              className='w-full justify-start gap-3 h-16'
                            >
                              <div
                                className='w-10 h-10 rounded-lg border-2 border-gray-300 flex-shrink-0'
                                style={{ backgroundColor: formData.color }}
                              />
                              <div className='text-left'>
                                <div className='font-medium text-sm'>
                                  {presetColors.find(
                                    c => c.value === formData.color
                                  )?.name || 'Custom'}
                                </div>
                                <div className='text-xs text-muted-foreground'>
                                  {formData.color}
                                </div>
                              </div>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className='w-64' align='start'>
                            <div className='space-y-3'>
                              {/* Preset Colors Grid */}
                              <div>
                                <label className='text-xs font-medium text-gray-600 mb-2 block'>
                                  Preset Colors
                                </label>
                                <div className='grid grid-cols-5 gap-2'>
                                  {presetColors.map(color => (
                                    <button
                                      key={color.value}
                                      type='button'
                                      onClick={() => {
                                        handleInputChange('color', color.value);
                                        setIsColorPopoverOpen(false);
                                      }}
                                      className={`w-10 h-10 rounded-lg border-2 hover:border-gray-400 transition-all hover:scale-105 ${
                                        color.value === '#FFFFFF'
                                          ? 'border-gray-300'
                                          : 'border-gray-200'
                                      } ${
                                        formData.color === color.value
                                          ? 'ring-2 ring-blue-500 ring-offset-2 scale-105'
                                          : ''
                                      }`}
                                      style={{ backgroundColor: color.value }}
                                      title={color.name}
                                    />
                                  ))}
                                </div>
                              </div>

                              {/* Custom Color Picker */}
                              <div className='border-t pt-3'>
                                <label className='text-xs font-medium text-gray-600 mb-2 block'>
                                  Custom Color
                                </label>
                                <input
                                  type='color'
                                  value={formData.color}
                                  onChange={e =>
                                    handleInputChange('color', e.target.value)
                                  }
                                  className='w-full h-10 rounded-lg cursor-pointer border-2 border-gray-200'
                                />
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='space-name' className='text-base'>
                        Name this space
                      </Label>
                      <Input
                        id='space-name'
                        placeholder='e.g. Office Renovation'
                        value={formData.name}
                        onChange={e =>
                          handleInputChange('name', e.target.value)
                        }
                        className='bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all placeholder:text-gray-400'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='space-description' className='text-base'>
                        Add a description (optional)
                      </Label>
                      <Textarea
                        id='space-description'
                        placeholder='e.g. Plans and scheduling for expanding the office'
                        value={formData.description}
                        onChange={e =>
                          handleInputChange('description', e.target.value)
                        }
                        className='bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all min-h-24 resize-none placeholder:text-gray-400'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label className='text-base font-medium text-gray-700'>
                        Location
                      </Label>
                      <LocationMapPicker
                        value={formData.locationData}
                        onChange={handleLocationChange}
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label
                        htmlFor='space-industry'
                        className='text-base font-medium text-gray-700'
                      >
                        Industry
                      </Label>
                      <Input
                        id='space-industry'
                        placeholder='e.g. Construction, Manufacturing, Healthcare'
                        value={formData.industry}
                        onChange={e =>
                          handleInputChange('industry', e.target.value)
                        }
                        className='bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all placeholder:text-gray-400'
                      />
                    </div>

                    {/* Custom Fields Section */}
                    <div className='pt-4 border-t space-y-4'>
                      <div className='flex items-center justify-between'>
                        <div className='space-y-1'>
                          <Label className='text-base font-medium text-gray-700'>
                            Custom Fields (optional)
                          </Label>
                          <p className='text-sm text-muted-foreground'>
                            Add custom fields to track additional information
                            for this space
                          </p>
                        </div>
                        <Switch
                          checked={isCustomFieldsEnabled}
                          onCheckedChange={setIsCustomFieldsEnabled}
                          className='data-[state=checked]:bg-blue-500'
                        />
                      </div>

                      {/* Show custom fields when enabled */}
                      {isCustomFieldsEnabled && (
                        <div className='space-y-3 pl-4 border-l-2 border-blue-200'>
                          {Object.keys(formData.customFields).length === 0 &&
                            !isAddingCustomField && (
                              <p className='text-sm text-muted-foreground italic'>
                                No custom fields added yet. Click "Add Field" to
                                create one.
                              </p>
                            )}

                          {/* Existing Custom Fields */}
                          {Object.entries(formData.customFields).map(
                            ([key, value]) => (
                              <div key={key} className='flex items-start gap-2'>
                                <div className='flex-1 space-y-1'>
                                  <Label
                                    htmlFor={`custom-${key}`}
                                    className='text-sm font-medium text-muted-foreground'
                                  >
                                    {key}
                                  </Label>
                                  <Input
                                    id={`custom-${key}`}
                                    value={value}
                                    onChange={e =>
                                      handleCustomFieldChange(
                                        key,
                                        e.target.value
                                      )
                                    }
                                    placeholder={`Enter ${key}...`}
                                    className='bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all'
                                  />
                                </div>
                                <Button
                                  type='button'
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => handleDeleteCustomField(key)}
                                  className='mt-6 h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50'
                                >
                                  <Trash2 className='h-4 w-4' />
                                </Button>
                              </div>
                            )
                          )}

                          {/* Inline Add New Field */}
                          {isAddingCustomField && (
                            <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3'>
                              <div className='space-y-2'>
                                <Label
                                  htmlFor='new-custom-field-name'
                                  className='text-sm font-medium'
                                >
                                  Field Name
                                </Label>
                                <Input
                                  id='new-custom-field-name'
                                  value={newCustomFieldName}
                                  onChange={e =>
                                    setNewCustomFieldName(e.target.value)
                                  }
                                  placeholder='e.g., Project Code, Budget, Manager'
                                  autoFocus
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      document
                                        .getElementById(
                                          'new-custom-field-value'
                                        )
                                        ?.focus();
                                    } else if (e.key === 'Escape') {
                                      handleCancelAddCustomField();
                                    }
                                  }}
                                  className='bg-white'
                                />
                              </div>
                              <div className='space-y-2'>
                                <Label
                                  htmlFor='new-custom-field-value'
                                  className='text-sm font-medium'
                                >
                                  Field Value
                                </Label>
                                <Input
                                  id='new-custom-field-value'
                                  value={newCustomFieldValue}
                                  onChange={e =>
                                    setNewCustomFieldValue(e.target.value)
                                  }
                                  placeholder='Enter value...'
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveNewCustomField();
                                    } else if (e.key === 'Escape') {
                                      handleCancelAddCustomField();
                                    }
                                  }}
                                  className='bg-white'
                                />
                              </div>
                              <div className='flex gap-2 justify-end'>
                                <Button
                                  type='button'
                                  variant='ghost'
                                  size='sm'
                                  onClick={handleCancelAddCustomField}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type='button'
                                  size='sm'
                                  onClick={handleSaveNewCustomField}
                                  disabled={!newCustomFieldName.trim()}
                                  className='bg-green-600 hover:bg-green-700'
                                >
                                  <Check className='h-4 w-4 mr-2' />
                                  Add Field
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Add Field Button */}
                          {!isAddingCustomField && (
                            <Button
                              type='button'
                              variant='outline'
                              onClick={handleAddCustomField}
                              className='w-full border-dashed border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
                            >
                              <Plus className='h-4 w-4 mr-2' />
                              Add Field
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Choose Tools */}
              {currentStep === 2 && (
                <motion.div
                  key='step2'
                  initial={{
                    opacity: 0,
                    x: animationDirection === 1 ? 100 : -100
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{
                    opacity: 0,
                    x: animationDirection === 1 ? -100 : 100
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className='max-w-5xl mx-auto space-y-4'
                >
                  <div className="flex items-center justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const nonEssentialTools = spaceTools.filter(tool => !tool.essential);
                        const allNonEssentialEnabled = nonEssentialTools.every(tool => formData.tools[tool.id]);
                        const newTools = { ...formData.tools };
                        spaceTools.forEach(tool => {
                          // Keep essential tools enabled, toggle non-essential ones
                          if (!tool.essential) {
                            newTools[tool.id] = !allNonEssentialEnabled;
                          }
                        });
                        setFormData(prev => ({ ...prev, tools: newTools }));
                      }}
                    >
                      {spaceTools.filter(tool => !tool.essential).every(tool => formData.tools[tool.id]) ? 'Disable All' : 'Enable All'}
                    </Button>
                  </div>
                  <div className='flex justify-center w-full'>
                    <div className='flex flex-wrap justify-center gap-4 w-full max-w-7xl'>
                      {spaceTools.map(tool => (
                        <div key={tool.id} className='flex w-full sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] max-w-[340px]'>
                          <ToolCard
                            tool={tool}
                            isEnabled={formData.tools[tool.id]}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Add Team */}
              {currentStep === 3 && (
                <motion.div
                  key='step3'
                  initial={{
                    opacity: 0,
                    x: animationDirection === 1 ? 100 : -100
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 100 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className='max-w-3xl mx-auto space-y-6'
                >
                  {/* Team member selection - All spaces are invite only */}
                  <div className='space-y-4'>
                      <Label className='text-base'>
                        Select team members to invite
                      </Label>

                      {/* Search Input */}
                      <div className='relative'>
                        <div className='relative'>
                          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                          <Input
                            type='text'
                            placeholder='Search team members...'
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onFocus={() => setIsDropdownOpen(true)}
                            className='pl-10 pr-4'
                          />
                        </div>

                        {/* Selected Count */}
                        {formData.teamMembers.length > 0 && (
                          <div className='mt-2'>
                            <p className='text-sm text-muted-foreground'>
                              {formData.teamMembers.length} member
                              {formData.teamMembers.length !== 1
                                ? 's'
                                : ''}{' '}
                              selected
                            </p>
                          </div>
                        )}

                        {/* Dropdown */}
                        <AnimatePresence>
                          {isDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3, ease: 'easeInOut' }}
                              className='w-full mt-1 bg-white border rounded-lg shadow-lg overflow-hidden'
                            >
                              {isLoadingTeam ? (
                                <div className='flex items-center justify-center py-8'>
                                  <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                                  <span className='ml-2 text-sm text-muted-foreground'>
                                    Loading team members...
                                  </span>
                                </div>
                              ) : (
                                <ScrollArea className='h-[300px]'>
                                  <div className='p-2'>
                                    {teamMembers
                                      .filter(member => {
                                        const query = searchQuery.toLowerCase();
                                        return (
                                          member.name
                                            .toLowerCase()
                                            .includes(query) ||
                                          member.email
                                            .toLowerCase()
                                            .includes(query)
                                        );
                                      })
                                      .map(member => {
                                        const isCreator =
                                          member.id === userProfile?.id;
                                        const isSelected =
                                          formData.teamMembers.some(
                                            m => m.id === member.id
                                          );

                                        return (
                                          <div
                                            key={member.id}
                                            onClick={() =>
                                              !isCreator &&
                                              handleTeamMemberToggle(member)
                                            }
                                            className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                                              isCreator
                                                ? 'opacity-50 cursor-not-allowed'
                                                : ''
                                            }`}
                                          >
                                            <div className='flex items-center justify-center w-5 h-5'>
                                              {(isSelected || isCreator) && (
                                                <Check className='h-4 w-4 text-green-600' />
                                              )}
                                            </div>
                                            <Avatar className='h-8 w-8'>
                                              <AvatarImage
                                                src={member.avatar}
                                                alt={member.name}
                                              />
                                              <AvatarFallback className='text-xs'>
                                                {member.initials}
                                              </AvatarFallback>
                                            </Avatar>
                                            <div className='flex-1 min-w-0'>
                                              <p className='font-medium truncate'>
                                                {member.name}
                                                {isCreator && (
                                                  <span className='ml-2 text-xs text-green-600 font-medium'>
                                                    (You - Owner)
                                                  </span>
                                                )}
                                              </p>
                                              <p className='text-sm text-muted-foreground truncate'>
                                                {member.email}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    {teamMembers.filter(member => {
                                      const query = searchQuery.toLowerCase();
                                      return (
                                        member.name
                                          .toLowerCase()
                                          .includes(query) ||
                                        member.email
                                          .toLowerCase()
                                          .includes(query)
                                      );
                                    }).length === 0 && (
                                      <div className='text-center py-4'>
                                        <p className='text-sm text-muted-foreground'>
                                          No members found matching "
                                          {searchQuery}"
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </ScrollArea>
                              )}
                              <div className='border-t p-2'>
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  className='w-full'
                                  onClick={() => setIsDropdownOpen(false)}
                                >
                                  Done
                                </Button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Selected Members */}
                      {formData.teamMembers.length > 0 && (
                        <div className='space-y-2'>
                          <Label className='text-sm text-muted-foreground'>
                            Selected members:
                          </Label>
                          <div className='flex flex-wrap gap-2'>
                            {formData.teamMembers.map(member => (
                              <Badge
                                key={member.id}
                                variant='secondary'
                                className='pl-2 pr-1 py-1'
                              >
                                <span className='mr-1'>{member.name}</span>
                                <button
                                  onClick={() => handleTeamMemberToggle(member)}
                                  className='ml-1 rounded-full hover:bg-gray-300 p-0.5'
                                >
                                  <X className='h-3 w-3' />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className='pt-2'>
                        <p className='text-sm text-muted-foreground'>
                          You can always invite more people later.
                        </p>
                      </div>
                    </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Navigation */}
          <div className='px-8 py-4 bg-gray-50 border-t flex justify-between'>
            <div>
              {currentStep > 1 && (
                <Button
                  variant='outline'
                  onClick={handlePrevious}
                  className='flex items-center gap-2'
                >
                  <ArrowLeft className='w-4 h-4' />
                  Previous
                </Button>
              )}
            </div>

            <div>
              {currentStep < 3 ? (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    onClick={handleNext}
                    className='bg-black hover:bg-gray-900 text-white flex items-center gap-2 shadow-lg hover:shadow-xl transition-all'
                    disabled={!canProceed()}
                  >
                    Next
                    <ArrowRight className='w-4 h-4' />
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    onClick={handleCreateSpace}
                    className='bg-black hover:bg-gray-900 text-white shadow-lg hover:shadow-xl transition-all px-6'
                    disabled={!canProceed() || isCreating}
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Creating...
                      </>
                    ) : (
                      'Create Space'
                    )}
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* AI Image Generator Dialog */}
      <Dialog open={isAiGeneratorOpen} onOpenChange={handleCloseAiGenerator}>
        <DialogContent className='w-[95vw] !max-w-[95vw] sm:!max-w-[95vw] max-h-[90vh] overflow-hidden p-0'>
          <div className='grid grid-cols-1 lg:grid-cols-2 h-full'>
            {/* Left Pane - Controls */}
            <div className='flex flex-col max-h-[90vh]'>
              <DialogHeader className='px-6 pt-6 pb-4 border-b'>
                <DialogTitle className='flex items-center gap-2'>
                  <Wand2 className='h-5 w-5 text-purple-600' />
                  Generate Avatar with AI
                </DialogTitle>
                <DialogDescription>
                  Describe what you want your space avatar to look like, and AI
                  will generate it for you.
                </DialogDescription>
              </DialogHeader>

              <ScrollArea className='flex-1 overflow-y-auto'>
                <div className='space-y-6 px-6 py-4'>
                  {/* Prompt Input */}
                  <div className='space-y-2'>
                    <Label
                      htmlFor='ai-prompt'
                      className='text-base font-medium'
                    >
                      Image Description
                    </Label>
                    <Textarea
                      id='ai-prompt'
                      placeholder='e.g., A modern construction site with cranes and buildings, minimalist style, circular logo'
                      value={aiPrompt}
                      onChange={e => setAiPrompt(e.target.value)}
                      className='min-h-32 resize-none'
                      disabled={isGenerating}
                    />
                    <p className='text-xs text-muted-foreground'>
                      Describe the style, elements, and mood you want in your
                      avatar image.
                    </p>
                  </div>

                  {/* Style Selection */}
                  <div className='space-y-3'>
                    <Label className='text-base font-medium'>Image Style</Label>
                    <p className='text-xs text-muted-foreground mb-3'>
                      Choose the visual style for your avatar
                    </p>
                    <div className='grid grid-cols-2 gap-3'>
                      {imageStyles.map(style => (
                        <button
                          key={style.id}
                          type='button'
                          onClick={() => setAiStyle(style.id)}
                          disabled={isGenerating}
                          className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${
                            aiStyle === style.id
                              ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-500 ring-offset-2'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className='flex items-start justify-between'>
                            <div className='flex-1'>
                              <div className='font-medium text-sm mb-1'>
                                {style.name}
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                {style.description}
                              </div>
                            </div>
                            {aiStyle === style.id && (
                              <Check className='h-5 w-5 text-purple-600 flex-shrink-0 ml-2' />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Selection */}
                  <div className='space-y-3'>
                    <Label className='text-base font-medium'>
                      Colors to Include (Optional)
                    </Label>
                    <div className='grid grid-cols-5 gap-3'>
                      {presetColors.map(color => (
                        <button
                          key={color.value}
                          type='button'
                          onClick={() => handleColorToggle(color.value)}
                          disabled={isGenerating}
                          className={`h-12 rounded-lg border-2 transition-all hover:scale-105 ${
                            color.value === '#FFFFFF'
                              ? 'border-gray-300'
                              : 'border-gray-200'
                          } ${
                            aiColors.includes(color.value)
                              ? 'ring-2 ring-purple-500 ring-offset-2 scale-105'
                              : ''
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        >
                          {aiColors.includes(color.value) && (
                            <div className='flex items-center justify-center'>
                              <Check className='h-5 w-5 text-purple-600 font-bold drop-shadow-md' />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    {aiColors.length > 0 && (
                      <div className='flex flex-wrap gap-2 mt-2'>
                        {aiColors.map(colorValue => {
                          const color = presetColors.find(
                            c => c.value === colorValue
                          );
                          return (
                            <Badge
                              key={colorValue}
                              variant='secondary'
                              className='pl-2 pr-1 py-1'
                            >
                              <div
                                className='w-3 h-3 rounded-full mr-1.5 border'
                                style={{ backgroundColor: colorValue }}
                              />
                              <span>{color?.name || 'Custom'}</span>
                              <button
                                onClick={() => handleColorToggle(colorValue)}
                                className='ml-1 rounded-full hover:bg-gray-300 p-0.5'
                                disabled={isGenerating}
                              >
                                <X className='h-3 w-3' />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Include Space Details Toggles */}
                  <div className='space-y-3'>
                    <Label className='text-base font-medium'>
                      Include Space Details
                    </Label>

                    <div className='flex items-center justify-between p-3 bg-gray-50 rounded-lg border'>
                      <div className='space-y-0.5'>
                        <Label
                          htmlFor='include-name'
                          className='font-medium cursor-pointer text-sm'
                        >
                          Include Space Name
                        </Label>
                        <p className='text-xs text-muted-foreground'>
                          {formData.name
                            ? `Add "${formData.name}" to the prompt`
                            : 'No name entered yet'}
                        </p>
                      </div>
                      <Switch
                        id='include-name'
                        checked={includeSpaceName}
                        onCheckedChange={setIncludeSpaceName}
                        disabled={isGenerating}
                      />
                    </div>

                    <div className='flex items-center justify-between p-3 bg-gray-50 rounded-lg border'>
                      <div className='space-y-0.5'>
                        <Label
                          htmlFor='include-description'
                          className='font-medium cursor-pointer text-sm'
                        >
                          Include Space Description
                        </Label>
                        <p className='text-xs text-muted-foreground'>
                          {formData.description
                            ? `Add description to the prompt`
                            : 'No description entered yet'}
                        </p>
                      </div>
                      <Switch
                        id='include-description'
                        checked={includeSpaceDescription}
                        onCheckedChange={setIncludeSpaceDescription}
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                </div>
              </ScrollArea>

              {/* Footer with action buttons */}
              <div className='px-6 py-4 border-t bg-gray-50'>
                <div className='flex items-center justify-between'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => handleCloseAiGenerator(false)}
                    disabled={isGenerating}
                  >
                    Cancel
                  </Button>

                  <div className='flex gap-2'>
                    {generatedImageUrl && (
                      <>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={handleGenerateAiImage}
                          disabled={!aiPrompt.trim() || isGenerating}
                          className='border-purple-300 text-purple-700 hover:bg-purple-50'
                        >
                          {isGenerating ? (
                            <>
                              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className='mr-2 h-4 w-4' />
                              Regenerate
                            </>
                          )}
                        </Button>
                        <Button
                          type='button'
                          onClick={handleSaveGeneratedImage}
                          disabled={isGenerating}
                          className='bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white'
                        >
                          <Check className='mr-2 h-4 w-4' />
                          Save Image
                        </Button>
                      </>
                    )}

                    {!generatedImageUrl && (
                      <Button
                        type='button'
                        onClick={handleGenerateAiImage}
                        disabled={!aiPrompt.trim() || isGenerating}
                        className='bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white'
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className='mr-2 h-4 w-4' />
                            Generate Image
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Pane - Image Preview */}
            <div className='bg-gradient-to-br from-purple-50 to-blue-50 border-l flex items-center justify-center p-8'>
              {isGenerating ? (
                <div className='flex flex-col items-center gap-4'>
                  <Loader2 className='h-16 w-16 text-purple-600 animate-spin' />
                  <p className='text-lg font-medium text-gray-700'>
                    Generating your avatar...
                  </p>
                  <p className='text-sm text-gray-500'>
                    This may take a few moments
                  </p>
                </div>
              ) : generatedImageUrl ? (
                <div className='flex flex-col items-center gap-6 w-full max-w-lg'>
                  <div className='relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl border-4 border-white'>
                    <img
                      src={generatedImageUrl}
                      alt='Generated avatar'
                      className='w-full h-full object-cover'
                    />
                  </div>
                  <div className='text-center'>
                    <p className='text-lg font-semibold text-gray-800 mb-1'>
                      Avatar Generated!
                    </p>
                    <p className='text-sm text-gray-600'>
                      Review your avatar and save it, or adjust settings and
                      regenerate
                    </p>
                  </div>
                </div>
              ) : (
                <div className='flex flex-col items-center gap-4 text-center max-w-md'>
                  <div className='w-32 h-32 rounded-full bg-white/50 flex items-center justify-center'>
                    <Wand2 className='h-16 w-16 text-purple-400' />
                  </div>
                  <div>
                    <p className='text-lg font-medium text-gray-700 mb-2'>
                      Preview Your Avatar
                    </p>
                    <p className='text-sm text-gray-500'>
                      Configure your settings on the left and click Generate to
                      see your AI-created avatar here
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Cropper Modal */}
      <ImageCropper
        isOpen={isCropperOpen}
        onClose={() => {
          setIsCropperOpen(false);
          setTempImageSrc('');
        }}
        imageSrc={tempImageSrc}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
        cropShape='round'
      />
    </AppShell3>
  );
};

export default CreateNewSpace;
